import { FilterBar, type FilterConfig, createFilterConfig } from '@/Components/filterbar';
import ZnglyContents from '@/Components/znglyContents/ZnglyContents';
import { type RouteTo, useNavigate } from '@/Utility/hooks';
import { updateCategoryPinnedContentMutation } from '@/graphql/mutations/Category.mutation';
import { addZnglistContentMutation } from '@/graphql/mutations/Znglist.mutation';
import { PostStatusEnum } from '@/graphql/types';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Container, LinearProgress, Modal, Stack } from '@mui/material';
import { type ComponentProps, useEffect, useMemo, useState } from 'react';
import type { AddFromCatalogSearch } from '.';
import { useAfcStore } from './useAfcStore';

import './AddFromCatalog.less';

const AddFromCatalogFilterConfig = createFilterConfig(
  {
    routeId: 'key:add-from-catalog',
  },
  {
    search: true,
    visibility: true,
    mediaName: true,
    order: true,
    category: true,
    author: true,
    tags: true,
    date: true,
  }
);

export type AddFromCatalogFilterConfigType = typeof AddFromCatalogFilterConfig;

type Props = {
  afcType: 'znglist' | 'category';
  limit: number;
  toId: string;
  navBack: {
    to: Parameters<ReturnType<typeof useNavigate>['navigateBack']>[0];
    opts?: Parameters<ReturnType<typeof useNavigate>['navigateBack']>[1];
  };
} & AddFromCatalogSearch;

export const AddFromCatalog = ({ afcType, limit = 100, toId, initIds, ...props }: Props) => {
  const [loading, setLoading] = useState(false);
  const { navigateBack } = useNavigate();
  const selectedIds = useAfcStore((state) => state.selectedIds);

  // Initialize store and clean up on unmount
  useEffect(() => {
    useAfcStore.setState({ afcType, toId, initIds, limit });
    return () => useAfcStore.getState().reset();
  }, []);

  // Handle form submission
  const handleSubmit = () => {
    setLoading(true);
    const newIds = [...new Set([...initIds, ...selectedIds])];

    const mutationOptions = {
      onCompleted: () => {
        setLoading(false);
        navigateBack(props.navBack.to, props.navBack?.opts);
      },
    };

    if (afcType === 'znglist') {
      addZnglistContentMutation({ input: { id: toId, znglyContents: newIds } }, mutationOptions);
    } else {
      updateCategoryPinnedContentMutation({ input: { id: toId, pinnedContent: newIds } }, mutationOptions);
    }
  };

  const defaultWhereArgs: ComponentProps<typeof ZnglyContents>['defaultWhereArgs'] = useMemo(() => {
    return {
      notIn: initIds,
      stati: [PostStatusEnum.Publish, PostStatusEnum.Hidden, PostStatusEnum.HiddenBS],
      ...(afcType === 'category' ? { categoryIn: [toId] } : {}),
    };
  }, [afcType, toId, initIds]);

  const buttonText = selectedIds?.length === limit ? 'Finished' : 'Add From Catalog';

  return (
    <Modal className="AddFromCatalogModal" open>
      <Box className="ZnglyModal Big">
        <FilterBar
          {...AddFromCatalogFilterConfig.config}
          left={{
            configs: [
              {
                type: 'custom',
                component: (
                  <Button
                    className="z-btn-6"
                    onClick={() => navigateBack(props?.navBack?.to as RouteTo, props.navBack?.opts)}
                  >
                    Back
                  </Button>
                ),
              },
              { type: 'search' },
            ],
            stackProps: { flexWrap: 'nowrap', flexShrink: 0, width: 'auto' },
          }}
          right={{
            configs: [
              { type: 'mediaType', displayName: 'Media' },
              {
                type: 'visibility',
                displayName: 'Visibility',
                hideDraft: true,
                hideTrash: true,
                hidePending: true,
                showHiddenBS: true,
              },
              { type: 'author' },
              ...(afcType === 'category' ? [] : [{ type: 'category' }]),
              { type: 'date' },
              { type: 'order' },
              { type: 'myZngly' },
              { type: 'clearBtn' },
            ].filter(Boolean) as FilterConfig[],
            stackProps: { flexWrap: 'wrap', justifyContent: 'flex-end' },
          }}
        />

        {loading && (
          <LinearProgress sx={{ width: '100%', height: '3px', position: 'sticky', top: '0', zIndex: 100, m: 0.5 }} />
        )}

        <Container>
          <ZnglyContents
            className="AddFromCatalogContent"
            view="select"
            defaultWhereArgs={defaultWhereArgs}
            filterOptions={AddFromCatalogFilterConfig}
          />
        </Container>

        <Stack direction="row" className="bottom-bar action-bar-sticky bottom">
          <Button
            className="z-btn-5"
            startIcon={<AddIcon />}
            onClick={handleSubmit}
            disabled={loading || selectedIds.length === 0}
          >
            <p>{buttonText}</p>
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default AddFromCatalog;
